import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import RestockOrders from '../../components/admin/RestockOrders';
import { invPhoto } from '../../components/admin/inventory/photos';
import InventoryActivityTab from '../../components/admin/inventory/InventoryActivityTab';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Bar, BarChart, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis,
} from 'recharts';
import {
  Package, PackageX, DollarSign, Plus, Search, ExternalLink, MapPin,
  ArrowUpCircle, Wrench, AlertTriangle, Loader2, RotateCw, TrendingUp,
} from 'lucide-react';
import AddProductDialog from '../../components/admin/inventory/AddProductDialog';
import AddEquipmentDialog from '../../components/admin/inventory/AddEquipmentDialog';
import RestockDialog from '../../components/admin/inventory/RestockDialog';
import AdjustStockDialog from '../../components/admin/inventory/AdjustStockDialog';
import LogPastPurchaseDialog from '../../components/admin/inventory/LogPastPurchaseDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ['Chemicals', 'Tools', 'Consumables'];
const STOCK_STATUSES = ['In Stock', 'Low Stock', 'Out of Stock'];
const EQ_CONDITIONS = ['Excellent', 'Good', 'Fair', 'Poor'];
const EQ_STATUSES = ['Available', 'In Use', 'Needs Repair', 'Retired'];

const StatusBadge = ({ status }) => {
  const cfg = {
    'In Stock': 'bg-emerald-50 text-emerald-700 border-emerald-200',
    'Low Stock': 'bg-amber-50 text-amber-700 border-amber-200',
    'Out of Stock': 'bg-red-50 text-red-700 border-red-200',
  }[status] || 'bg-zinc-100 text-zinc-700 border-zinc-200';
  return <Badge variant="outline" className={`${cfg} text-[10px] font-semibold`}>{status}</Badge>;
};

const AdminInventoryPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [tab, setTab] = useState('overview');
  const [prodView, setProdView] = useState('cards');
  const [overview, setOverview] = useState(null);
  const [products, setProducts] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prodSearch, setProdSearch] = useState('');
  const [prodCategory, setProdCategory] = useState('all');
  const [prodStatus, setProdStatus] = useState('all');
  const [eqSearch, setEqSearch] = useState('');
  const [eqCondition, setEqCondition] = useState('all');
  const [eqStatus, setEqStatus] = useState('all');
  const [addProdOpen, setAddProdOpen] = useState(false);
  const [addEqOpen, setAddEqOpen] = useState(false);
  const [restockTarget, setRestockTarget] = useState(null);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [logPastTarget, setLogPastTarget] = useState(null); // { type: 'product'|'equipment', id, name }

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, pr, eq] = await Promise.all([
        axios.get(`${API_URL}/api/admin/inventory/overview`, { headers }),
        axios.get(`${API_URL}/api/admin/inventory/products`, { headers }),
        axios.get(`${API_URL}/api/admin/inventory/equipment`, { headers }),
      ]);
      setOverview(ov.data);
      setProducts(pr.data);
      setEquipment(eq.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load inventory');
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredProducts = useMemo(() => {
    let list = products;
    if (prodSearch) {
      const q = prodSearch.toLowerCase();
      list = list.filter(p => (p.name || '').toLowerCase().includes(q) || (p.brand || '').toLowerCase().includes(q));
    }
    if (prodCategory !== 'all') list = list.filter(p => p.category === prodCategory);
    if (prodStatus !== 'all') list = list.filter(p => p.stock_status === prodStatus);
    return list;
  }, [products, prodSearch, prodCategory, prodStatus]);

  const filteredEquipment = useMemo(() => {
    let list = equipment;
    if (eqSearch) {
      const q = eqSearch.toLowerCase();
      list = list.filter(e => (e.name || '').toLowerCase().includes(q));
    }
    if (eqCondition !== 'all') list = list.filter(e => e.condition === eqCondition);
    if (eqStatus !== 'all') list = list.filter(e => e.status === eqStatus);
    return list;
  }, [equipment, eqSearch, eqCondition, eqStatus]);

  const buyAgain = (p) => {
    if (p.purchase_url) {
      window.open(p.purchase_url, '_blank', 'noopener');
    } else if (p.vendor_name || p.store_address) {
      toast.info(`${p.vendor_name || ''}${p.store_address ? ` — ${p.store_address}` : ''}`, { duration: 6000 });
    } else {
      toast.error('No purchase URL or store info saved for this product');
    }
  };

  return (
    <div className="min-h-screen" data-testid="admin-inventory-page">
      <header className="gg-legacy-head">
        <div className="max-w-[1680px] mx-auto px-5 sm:px-9 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-lg sm:text-xl font-heading text-zinc-900 flex items-center gap-2">
              <Package className="w-5 h-5 text-[#F56F05]" /> Inventory
            </h1>
            <p className="text-[11px] sm:text-xs text-zinc-500">Products, equipment, purchases, and stock activity</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={fetchAll} disabled={loading} data-testid="inv-refresh"><RotateCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></Button>
            {tab === 'products' && (
              <Button size="sm" className="bg-[#F56F05] hover:bg-[#e06505] text-white" onClick={() => setAddProdOpen(true)} data-testid="inv-add-product">
                <Plus className="w-4 h-4 mr-1" /> Add Product
              </Button>
            )}
            {tab === 'equipment' && (
              <Button size="sm" className="bg-[#F56F05] hover:bg-[#e06505] text-white" onClick={() => setAddEqOpen(true)} data-testid="inv-add-equipment">
                <Plus className="w-4 h-4 mr-1" /> Add Equipment
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-[1680px] mx-auto px-3 sm:px-6 lg:px-8 py-6 space-y-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex w-full sm:w-auto sm:inline-flex overflow-x-auto justify-start">
            <TabsTrigger value="overview" data-testid="inv-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="products" data-testid="inv-tab-products">Products</TabsTrigger>
            <TabsTrigger value="restock" data-testid="inv-tab-restock">Restock</TabsTrigger>
            <TabsTrigger value="equipment" data-testid="inv-tab-equipment">Equipment</TabsTrigger>
            <TabsTrigger value="activity" data-testid="inv-tab-activity">Activity</TabsTrigger>
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="mt-4 space-y-4">
            {overview && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Card className="border-0 shadow-sm" data-testid="kpi-low-stock">
                    <CardContent className="p-4 flex items-start justify-between">
                      <div>
                        <p className="text-[10px] uppercase font-semibold text-zinc-500">Low Stock</p>
                        <p className="text-2xl font-extrabold text-amber-600">{overview.kpis.low_stock_count}</p>
                        <p className="text-[11px] text-zinc-500 mt-1">at or below reorder threshold</p>
                      </div>
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                    </CardContent>
                  </Card>
                  <Card className="border-0 shadow-sm" data-testid="kpi-out-of-stock">
                    <CardContent className="p-4 flex items-start justify-between">
                      <div>
                        <p className="text-[10px] uppercase font-semibold text-zinc-500">Out of Stock</p>
                        <p className="text-2xl font-extrabold text-red-600">{overview.kpis.out_of_stock_count}</p>
                        <p className="text-[11px] text-zinc-500 mt-1">zero quantity available</p>
                      </div>
                      <PackageX className="w-5 h-5 text-red-500" />
                    </CardContent>
                  </Card>
                  <Card className="border-0 shadow-sm" data-testid="kpi-monthly-spend">
                    <CardContent className="p-4 flex items-start justify-between">
                      <div>
                        <p className="text-[10px] uppercase font-semibold text-zinc-500">Monthly Supply Spend</p>
                        <p className="text-2xl font-extrabold text-zinc-900">${overview.kpis.monthly_supply_spend.toFixed(2)}</p>
                        <p className="text-[11px] text-zinc-500 mt-1">actual paid this month</p>
                      </div>
                      <DollarSign className="w-5 h-5 text-emerald-500" />
                    </CardContent>
                  </Card>
                </div>

                {/* 6-Month Spend Trend */}
                {overview.spend_trend?.length > 0 && (
                  <Card className="border-0 shadow-sm" data-testid="spend-trend-card">
                    <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                      <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
                        <TrendingUp className="w-4 h-4 text-emerald-500" /> 6-Month Supply Spend
                      </CardTitle>
                      <span className="text-[11px] text-zinc-400">
                        Total: ${overview.spend_trend.reduce((s, r) => s + r.spend, 0).toFixed(2)}
                      </span>
                    </CardHeader>
                    <CardContent>
                      <div style={{ width: '100%', height: 160 }}>
                        <ResponsiveContainer>
                          <BarChart data={overview.spend_trend} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 10, fill: '#a1a1aa' }} axisLine={false} tickLine={false} tickFormatter={(v) => v === 0 ? '0' : `$${v}`} />
                            <RTooltip
                              formatter={(v) => [`$${Number(v).toFixed(2)}`, 'Spend']}
                              cursor={{ fill: '#F56F0510' }}
                              contentStyle={{ borderRadius: 8, border: '1px solid #e4e4e7', fontSize: 12 }}
                            />
                            <Bar dataKey="spend" fill="#F56F05" radius={[6, 6, 0, 0]} maxBarSize={44} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}
                {/* Attention */}
                <Card className="border-0 shadow-sm" data-testid="inv-attention">
                  <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Inventory Attention</CardTitle></CardHeader>
                  <CardContent>
                    {overview.attention.length === 0 ? (
                      <p className="py-4 text-center text-sm text-zinc-500">All good — nothing needs attention right now.</p>
                    ) : (
                      <div className="space-y-2">
                        {overview.attention.map(p => (
                          <div key={p.id} className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-semibold text-sm text-zinc-900 truncate">{p.name}</p>
                                <StatusBadge status={p.stock_status} />
                              </div>
                              <p className="text-[11px] text-zinc-500">{p.quantity_on_hand} remaining · reorder at {p.reorder_level}</p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => navigate(`/admin/inventory/product/${p.id}`)} data-testid={`attn-view-${p.id}`}>View</Button>
                              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => buyAgain(p)} data-testid={`attn-buy-${p.id}`}><ExternalLink className="w-3 h-3" /></Button>
                              <Button size="sm" className="h-7 px-2 text-xs bg-[#F56F05] hover:bg-[#e06505] text-white" onClick={() => setRestockTarget(p)} data-testid={`attn-restock-${p.id}`}>Restock</Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Activity */}
                <Card className="border-0 shadow-sm" data-testid="inv-recent-activity">
                  <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Recent Inventory Activity</CardTitle></CardHeader>
                  <CardContent>
                    {overview.recent_activity.length === 0 ? (
                      <p className="py-4 text-center text-sm text-zinc-500">No activity yet.</p>
                    ) : (
                      <ol className="space-y-2">
                        {overview.recent_activity.map(a => <ActivityRow key={a.id} a={a} />)}
                      </ol>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* PRODUCTS */}
          <TabsContent value="products" className="mt-4 space-y-3">
            {/* Filters */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[160px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input value={prodSearch} onChange={e => setProdSearch(e.target.value)} placeholder="Search products..." className="pl-9" data-testid="prod-search" />
              </div>
              <Select value={prodCategory} onValueChange={setProdCategory}>
                <SelectTrigger className="w-36" data-testid="prod-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={prodStatus} onValueChange={setProdStatus}>
                <SelectTrigger className="w-36" data-testid="prod-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STOCK_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="ml-auto inline-flex rounded-md border border-[#dcded2] overflow-hidden" role="group" aria-label="Product display">
                {[['cards', 'Cards'], ['list', 'List']].map(([k, l]) => (
                  <button key={k} onClick={() => setProdView(k)} aria-pressed={prodView === k} data-testid={`prod-view-${k}`}
                    className={`px-3 py-1.5 text-xs ${prodView === k ? 'bg-[#252a20] text-white' : 'bg-white text-[#5f6455]'}`}>{l}</button>
                ))}
              </div>
            </div>

            {filteredProducts.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-12 text-center">
                  <Package className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                  <p className="text-sm text-zinc-500">{products.length === 0 ? 'No products yet — click "Add Product" to get started.' : 'No products match those filters.'}</p>
                </CardContent>
              </Card>
            ) : (
              prodView === 'cards' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="products-cards">
                  {filteredProducts.map(p => (
                    <article key={p.id} className="rounded-[10px] border border-[#e7e4db] bg-[#fffefa] overflow-hidden flex flex-col" data-testid={`prod-card-${p.id}`}>
                      <button onClick={() => navigate(`/admin/inventory/product/${p.id}`)} className="block bg-[#f4f2ed] aspect-square p-4" aria-label={`Open ${p.name}`}>
                        {p.photo_url ? <img src={invPhoto(p.photo_url)} alt={p.name} className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} data-testid={`prod-card-photo-${p.id}`} />
                          : <div className="w-full h-full grid place-items-center"><Package className="w-10 h-10 text-[#c9c6b9]" /></div>}
                      </button>
                      <div className="p-4 flex-1 flex flex-col">
                        <div className="flex items-start justify-between gap-2"><p className="text-[10px] tracking-[0.12em] font-bold text-[#7a7c6d]">{(p.brand || p.category || '').toUpperCase()}</p><StatusBadge status={p.stock_status} /></div>
                        <h3 className="text-sm font-semibold text-[#20211d] mt-1.5 leading-snug">{p.name}</h3>
                        <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                          <div className="rounded bg-[#f4f2ed] py-2"><strong className="block text-lg font-medium" data-testid={`prod-card-qty-${p.id}`}>{p.quantity_on_hand}</strong><small className="text-[10px] text-[#77776e]">On hand</small></div>
                          <div className="rounded bg-[#f4f2ed] py-2"><strong className="block text-lg font-medium">{p.reorder_level}</strong><small className="text-[10px] text-[#77776e]">Minimum</small></div>
                          <div className="rounded bg-[#f4f2ed] py-2"><strong className="block text-lg font-medium">${(p.current_replacement_cost || 0).toFixed(2)}</strong><small className="text-[10px] text-[#77776e]">Replace</small></div>
                        </div>
                        <p className="text-[11px] text-[#77776e] mt-3 flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.current_location || 'No location'}{p.vendor_name ? ` · ${p.vendor_name}` : ''}</p>
                        <div className="flex items-center gap-1 mt-auto pt-3">
                          <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={() => setRestockTarget(p)} data-testid={`prod-card-restock-${p.id}`}>Restock</Button>
                          <Button size="sm" variant="outline" className="h-8 px-2 text-xs" onClick={() => setAdjustTarget(p)} data-testid={`prod-card-adjust-${p.id}`}>Count / adjust</Button>
                          <Button size="sm" variant="ghost" className="h-8 px-2 text-xs" onClick={() => buyAgain(p)} title="Buy again" data-testid={`prod-card-buy-${p.id}`}><ExternalLink className="w-3.5 h-3.5" /></Button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              ) : <>
                {/* Desktop table */}
                <Card className="border-0 shadow-sm overflow-hidden hidden md:block">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="products-table">
                      <thead className="bg-zinc-50 text-[10px] uppercase text-zinc-500">
                        <tr>
                          <th className="text-left px-3 py-2">Photo</th>
                          <th className="text-left px-3 py-2">Product</th>
                          <th className="text-left px-3 py-2">Brand</th>
                          <th className="text-left px-3 py-2">Category</th>
                          <th className="text-right px-3 py-2">Qty</th>
                          <th className="text-right px-3 py-2">Reorder</th>
                          <th className="text-left px-3 py-2">Location</th>
                          <th className="text-right px-3 py-2">Repl. Cost</th>
                          <th className="text-center px-3 py-2">Status</th>
                          <th className="text-right px-3 py-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProducts.map(p => (
                          <tr key={p.id} className="border-t border-zinc-100 hover:bg-zinc-50/50 cursor-pointer"
                              onClick={() => navigate(`/admin/inventory/product/${p.id}`)}
                              data-testid={`prod-row-${p.id}`}>
                            <td className="px-3 py-2">
                              {p.photo_url ? <img src={invPhoto(p.photo_url)} alt="" className="w-10 h-10 rounded-lg object-cover" /> : <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center"><Package className="w-4 h-4 text-zinc-400" /></div>}
                            </td>
                            <td className="px-3 py-2 font-semibold text-zinc-900">{p.name}</td>
                            <td className="px-3 py-2 text-zinc-600">{p.brand || '—'}</td>
                            <td className="px-3 py-2 text-zinc-600">{p.category}</td>
                            <td className="px-3 py-2 text-right font-semibold">{p.quantity_on_hand}</td>
                            <td className="px-3 py-2 text-right text-zinc-500">{p.reorder_level}</td>
                            <td className="px-3 py-2 text-zinc-600 flex items-center gap-1"><MapPin className="w-3 h-3 text-zinc-400" /> {p.current_location || '—'}</td>
                            <td className="px-3 py-2 text-right text-zinc-700">${(p.current_replacement_cost || 0).toFixed(2)}</td>
                            <td className="px-3 py-2 text-center"><StatusBadge status={p.stock_status} /></td>
                            <td className="px-3 py-2 text-right" onClick={e => e.stopPropagation()}>
                              <div className="flex items-center gap-1 justify-end">
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setRestockTarget(p)} data-testid={`prod-restock-${p.id}`}>Restock</Button>
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => buyAgain(p)} data-testid={`prod-buy-${p.id}`}><ExternalLink className="w-3.5 h-3.5" /></Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>

                {/* Mobile cards */}
                <div className="md:hidden space-y-2" data-testid="products-cards-mobile">
                  {filteredProducts.map(p => (
                    <Card key={p.id} className="border-0 shadow-sm" data-testid={`prod-card-mobile-${p.id}`}>
                      <CardContent className="p-3">
                        <button onClick={() => navigate(`/admin/inventory/product/${p.id}`)} className="w-full text-left flex items-start gap-3">
                          {p.photo_url ? <img src={invPhoto(p.photo_url)} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" /> : <div className="w-12 h-12 rounded-lg bg-zinc-100 flex items-center justify-center flex-shrink-0"><Package className="w-5 h-5 text-zinc-400" /></div>}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="font-semibold text-sm text-zinc-900 truncate">{p.name}</p>
                              <StatusBadge status={p.stock_status} />
                            </div>
                            <p className="text-[11px] text-zinc-500 truncate">{p.brand ? `${p.brand} · ` : ''}{p.category}</p>
                            <p className="text-[11px] text-zinc-500 mt-1">Stock: <b>{p.quantity_on_hand}</b> · Reorder at {p.reorder_level} · ${(p.current_replacement_cost || 0).toFixed(2)}</p>
                            {p.current_location && <p className="text-[10px] text-zinc-400 flex items-center gap-0.5 mt-0.5"><MapPin className="w-2.5 h-2.5" /> {p.current_location}</p>}
                          </div>
                        </button>
                        <div className="flex items-center gap-1 mt-2 pt-2 border-t border-zinc-100">
                          <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={() => setRestockTarget(p)}>Restock</Button>
                          <Button size="sm" variant="outline" className="h-8 px-2 text-xs" onClick={() => buyAgain(p)}><ExternalLink className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="ghost" className="h-8 px-2 text-xs" onClick={() => setAdjustTarget(p)}>Adjust</Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="restock" className="mt-4"><RestockOrders headers={headers} /></TabsContent>
          <TabsContent value="activity" className="mt-4">{tab === 'activity' && <InventoryActivityTab headers={headers} />}</TabsContent>

          {/* EQUIPMENT */}
          <TabsContent value="equipment" className="mt-4 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[160px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input value={eqSearch} onChange={e => setEqSearch(e.target.value)} placeholder="Search equipment..." className="pl-9" data-testid="eq-search" />
              </div>
              <Select value={eqCondition} onValueChange={setEqCondition}>
                <SelectTrigger className="w-36" data-testid="eq-condition"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All conditions</SelectItem>
                  {EQ_CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={eqStatus} onValueChange={setEqStatus}>
                <SelectTrigger className="w-36" data-testid="eq-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {EQ_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {filteredEquipment.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-12 text-center">
                  <Wrench className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                  <p className="text-sm text-zinc-500">{equipment.length === 0 ? 'No equipment yet — click "Add Equipment" to get started.' : 'No equipment matches those filters.'}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="equipment-grid">
                {filteredEquipment.map(e => (
                  <Card key={e.id} className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => navigate(`/admin/inventory/equipment/${e.id}`)}
                        data-testid={`eq-card-${e.id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-start gap-3">
                        {e.photo_url ? <img src={invPhoto(e.photo_url)} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" /> : <div className="w-14 h-14 rounded-lg bg-zinc-100 flex items-center justify-center flex-shrink-0"><Wrench className="w-5 h-5 text-zinc-400" /></div>}
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-sm text-zinc-900 truncate">{e.name}</p>
                          <div className="flex items-center gap-1.5 flex-wrap mt-1">
                            <Badge variant="outline" className="text-[10px]">{e.condition}</Badge>
                            <Badge variant="outline" className="text-[10px]">{e.status}</Badge>
                          </div>
                          <p className="text-[11px] text-zinc-500 mt-1">
                            {e.current_location && <><MapPin className="inline w-2.5 h-2.5" /> {e.current_location}</>}
                          </p>
                          <p className="text-[11px] text-zinc-500">
                            Paid: ${(e.purchase_price || 0).toFixed(2)}
                            {e.current_replacement_cost != null && <> · Replace: ${e.current_replacement_cost.toFixed(2)}</>}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Dialogs */}
      <AddProductDialog open={addProdOpen} onOpenChange={setAddProdOpen} headers={headers} onSaved={fetchAll} />
      <AddEquipmentDialog open={addEqOpen} onOpenChange={setAddEqOpen} headers={headers} onSaved={fetchAll} />
      <RestockDialog product={restockTarget} onOpenChange={(o) => !o && setRestockTarget(null)} headers={headers} onSaved={fetchAll} />
      <AdjustStockDialog product={adjustTarget} onOpenChange={(o) => !o && setAdjustTarget(null)} headers={headers} onSaved={fetchAll} />
      <LogPastPurchaseDialog target={logPastTarget} onOpenChange={(o) => !o && setLogPastTarget(null)} headers={headers} onSaved={fetchAll} />

      {loading && !overview && (
        <div className="fixed inset-0 flex items-center justify-center bg-white/50">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
        </div>
      )}

    </div>
  );
};

const ActivityRow = ({ a }) => {
  const positive = (a.quantity_delta || 0) > 0;
  const label = a.txn_type === 'restock' ? 'Restocked' :
                a.txn_type === 'adjustment' ? `Adjustment — ${a.reason || ''}` :
                a.txn_type === 'past_purchase' ? 'Past Purchase' :
                a.txn_type === 'create' ? 'Initial Stock' :
                a.txn_type === 'equipment_purchase' ? 'Equipment Purchased' :
                a.txn_type === 'equipment_past_purchase' ? 'Equipment Past Purchase' :
                a.txn_type;
  return (
    <li className="flex items-center gap-3 py-1.5 border-b border-zinc-50 last:border-0" data-testid={`activity-row-${a.id}`}>
      {a.product_photo_url ? <img src={invPhoto(a.product_photo_url)} alt="" className="w-8 h-8 rounded-md object-cover flex-shrink-0" /> : <div className="w-8 h-8 rounded-md bg-zinc-100 flex-shrink-0 flex items-center justify-center"><ArrowUpCircle className={`w-4 h-4 ${positive ? 'text-emerald-500' : 'text-red-500'}`} /></div>}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-zinc-900 truncate">
          {a.quantity_delta != null && (
            <span className={`font-bold ${positive ? 'text-emerald-600' : 'text-red-600'}`}>
              {positive ? '+' : ''}{a.quantity_delta}
            </span>
          )}
          {' '}
          <b>{a.product_name || 'Equipment'}</b>
          <span className="text-zinc-500 text-xs"> · {label}</span>
        </p>
        <p className="text-[10px] text-zinc-400">
          {a.actor_name || 'Unknown'} · {new Date(a.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          {a.amount_paid != null && <> · <span className="text-zinc-700">${a.amount_paid.toFixed(2)}</span></>}
        </p>
      </div>
    </li>
  );
};

export default AdminInventoryPage;
